import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { getCommerceId } from '@/lib/ownership';

interface DayOrder {
  id: number;
  nomclient: string | null;
  totalttc: string | null;
  monaie: string | null;
  date_time: string | null;
  statut_com_vendeur: string | null;
}

interface DayMessage {
  id: number;
  message: string | null;
  created_at: string | null;
  is_read: number | null;
}

interface DayContact {
  id: number;
  name: string | null;
  subject: string | null;
  message: string | null;
  created_at: string | null;
  statusdemande: number | null;
}

interface DayAlert {
  id: number;
  type: string | null;
  message: string | null;
  created_at: string | null;
}

interface DayData {
  orders: DayOrder[];
  weekOrders: WeekOrder[];
  messages: DayMessage[];
  contacts: DayContact[];
  alerts: DayAlert[];
  pendingOrders: number;
  stockOut: number;
  grantedCourses: number;
}

interface WeekOrder {
  id: number;
  totalttc: string | null;
  date_time: string | null;
  monaie: string | null;
}

const EMPTY: DayData = {
  orders: [],
  weekOrders: [],
  messages: [],
  contacts: [],
  alerts: [],
  pendingOrders: 0,
  stockOut: 0,
  grantedCourses: 0,
};

function money(value: number, currency: string): string {
  return `${Math.round(value).toLocaleString('fr-FR')} ${currency}`;
}

function timeOf(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function DayActivityPage() {
  const { user } = useAuth();
  const commerceId = user ? getCommerceId(user) : null;
  const currency = user?.monaie || 'MAD';

  const [data, setData] = useState<DayData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    [],
  );

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const startISO = start.toISOString();
      const weekStart = new Date();
      weekStart.setHours(0, 0, 0, 0);
      weekStart.setDate(weekStart.getDate() - 6);
      const weekStartISO = weekStart.toISOString();

      const [ordersRes, weekRes, messagesRes, contactsRes, alertsRes, pendingRes, stockRes, accessRes] = await Promise.all([
        commerceId
          ? supabase
              .from('commande')
              .select('id, nomclient, totalttc, monaie, date_time, statut_com_vendeur')
              .eq('idvendeur', commerceId)
              .gte('date_time', startISO)
              .order('date_time', { ascending: false })
              .limit(40)
          : Promise.resolve({ data: [] as DayOrder[] }),
        commerceId
          ? supabase
              .from('commande')
              .select('id, totalttc, monaie, date_time')
              .eq('idvendeur', commerceId)
              .gte('date_time', weekStartISO)
              .order('date_time', { ascending: true })
              .limit(500)
          : Promise.resolve({ data: [] as WeekOrder[] }),
        supabase
          .from('messages')
          .select('id, message, created_at, is_read')
          .eq('receiver_id', user.id)
          .gte('created_at', startISO)
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('contact_messages')
          .select('id, name, subject, message, created_at, statusdemande')
          .eq('user_id', user.id)
          .gte('created_at', startISO)
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('notifications')
          .select('id, type, message, created_at')
          .eq('user_id', user.id)
          .eq('is_read', 0)
          .order('created_at', { ascending: false })
          .limit(10),
        commerceId
          ? supabase
              .from('commande')
              .select('id', { count: 'exact', head: true })
              .eq('idvendeur', commerceId)
              .eq('statut_com_vendeur', 'En attente')
          : Promise.resolve({ count: 0 }),
        commerceId
          ? supabase
              .from('product_items')
              .select('id', { count: 'exact', head: true })
              .eq('idcommerce', commerceId)
              .lte('stock', 0)
          : Promise.resolve({ count: 0 }),
        commerceId
          ? supabase
              .from('cours_access')
              .select('id', { count: 'exact', head: true })
              .eq('idcommerce', commerceId)
              .eq('status', 'active')
          : Promise.resolve({ count: 0 }),
      ]);

      setData({
        orders: (ordersRes.data || []) as DayOrder[],
        weekOrders: (weekRes.data || []) as WeekOrder[],
        messages: (messagesRes.data || []) as DayMessage[],
        contacts: (contactsRes.data || []) as DayContact[],
        alerts: (alertsRes.data || []) as DayAlert[],
        pendingOrders: pendingRes?.count || 0,
        stockOut: stockRes?.count || 0,
        grantedCourses: accessRes?.count || 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger votre activité du jour.');
    } finally {
      setLoading(false);
    }
  }, [user, commerceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const revenue = useMemo(
    () =>
      data.orders.reduce((sum, o) => {
        const amount = parseFloat(String(o.totalttc ?? '0'));
        return Number.isNaN(amount) ? sum : sum + amount;
      }, 0),
    [data.orders],
  );

  const newMessages = data.messages.length + data.contacts.length;
  const alertCount = data.alerts.length + data.stockOut + data.pendingOrders;

  // ── Récapitulatif « 7 derniers jours » (mini-graphique) ──────────────
  const weekSeries = useMemo(() => {
    const dayKey = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Array.from({ length: 7 }, (_, index) => {
      const offset = 6 - index;
      const d = new Date(today);
      d.setDate(d.getDate() - offset);
      return {
        key: dayKey(d),
        label: d.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', ''),
        nb: d.getDate(),
        total: 0,
        count: 0,
        isToday: offset === 0,
      };
    });
    const byKey = new Map(days.map((d) => [d.key, d]));
    data.weekOrders.forEach((order) => {
      if (!order.date_time) return;
      const parsed = new Date(order.date_time);
      if (Number.isNaN(parsed.getTime())) return;
      const bucket = byKey.get(dayKey(parsed));
      if (!bucket) return;
      const amount = parseFloat(String(order.totalttc ?? '0'));
      bucket.total += Number.isNaN(amount) ? 0 : amount;
      bucket.count += 1;
    });
    const max = Math.max(...days.map((d) => d.total), 1);
    const total = days.reduce((sum, d) => sum + d.total, 0);
    const orders = days.reduce((sum, d) => sum + d.count, 0);
    return { days, max, total, orders };
  }, [data.weekOrders]);

  const kpis = [
    {
      key: 'sales',
      icon: 'ri-shopping-bag-3-line',
      label: 'Ventes du jour',
      value: loading ? '…' : String(data.orders.length),
      sub: loading ? '' : money(revenue, currency),
      tone: 'bg-primary-100 text-primary-700',
      to: '/dashboard/orders',
    },
    {
      key: 'messages',
      icon: 'ri-chat-3-line',
      label: 'Messages & demandes',
      value: loading ? '…' : String(newMessages),
      sub: 'reçus aujourd\u2019hui',
      tone: 'bg-accent-100 text-accent-700',
      to: '/dashboard/messages',
    },
    {
      key: 'alerts',
      icon: 'ri-alarm-warning-line',
      label: 'Alertes à traiter',
      value: loading ? '…' : String(alertCount),
      sub: `${data.pendingOrders} commande(s) en attente`,
      tone: 'bg-secondary-100 text-secondary-700',
      to: '/dashboard/gestion-stock',
    },
  ];

  return (
    <div className="p-4 md:p-6">
      {/* En-tête */}
      <div className="rounded-xl border border-background-200/70 bg-background-50 p-5 md:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-widest text-primary-600">
              <i className="ri-sun-line mr-1"></i>Mon activité du jour
            </p>
            <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mt-1">
              Bonjour {user?.name?.split(' ')[0] || ''}, voici votre journée
            </h1>
            <p className="text-sm text-foreground-500 mt-1 capitalize">{todayLabel}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => void load()}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
              Actualiser
            </button>
            <Link
              to="/dashboard"
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-dashboard-line"></i>
              Tableau de bord
            </Link>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700 flex-1">
            <i className="ri-error-warning-line mr-1.5"></i>
            {error}
          </p>
          <button
            onClick={() => void load()}
            className="px-4 py-2 rounded-full bg-red-600 text-white text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
        {kpis.map((kpi) => (
          <Link
            key={kpi.key}
            to={kpi.to}
            className="group bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 hover:border-primary-300/70 transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between mb-3">
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${kpi.tone}`}>
                <i className={`${kpi.icon} text-lg`}></i>
              </span>
              <i className="ri-arrow-right-line text-sm text-foreground-300 opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </div>
            <p className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
              {loading ? <span className="inline-block w-14 h-7 rounded bg-background-200/70 animate-pulse"></span> : kpi.value}
            </p>
            <p className="text-xs text-foreground-500 mt-1">{kpi.label}</p>
            {kpi.sub && <p className="text-[11px] text-foreground-400 mt-0.5">{kpi.sub}</p>}
          </Link>
        ))}
      </div>

      {/* Récapitulatif 7 derniers jours */}
      <section className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
              <i className="ri-line-chart-line text-accent-500"></i>
              7 derniers jours
            </h2>
            <p className="text-[11px] text-foreground-400 mt-0.5">
              Encaissé par jour, du plus ancien à aujourd&apos;hui.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-bold font-heading text-foreground-950">{money(weekSeries.total, currency)}</p>
              <p className="text-[10px] text-foreground-400">{weekSeries.orders} vente(s)</p>
            </div>
            <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-bar-chart-2-line"></i>
            </span>
          </div>
        </div>

        <div className="flex items-end gap-1.5 md:gap-2.5 h-28">
          {weekSeries.days.map((day) => {
            const h = Math.max(day.total > 0 ? 8 : 3, Math.round((day.total / weekSeries.max) * 100));
            return (
              <div key={day.key} className="flex-1 min-w-0 flex flex-col items-center justify-end gap-1.5 h-full">
                <span className="text-[9px] md:text-[10px] font-medium text-foreground-500 tabular-nums truncate max-w-full">
                  {day.total > 0 ? Math.round(day.total).toLocaleString('fr-FR') : ''}
                </span>
                <div
                  className={`w-full rounded-t-md transition-all duration-300 ${
                    day.isToday ? 'bg-accent-500' : day.total > 0 ? 'bg-primary-300' : 'bg-background-200/70'
                  }`}
                  style={{ height: `${h}%` }}
                  title={`${day.label} ${day.nb} — ${money(day.total, currency)} · ${day.count} vente(s)`}
                ></div>
                <span className={`text-[9px] md:text-[10px] capitalize ${day.isToday ? 'text-accent-700 font-semibold' : 'text-foreground-400'}`}>
                  {day.label}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Ventes du jour */}
        <section className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-background-200/70">
            <h2 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
              <i className="ri-money-dollar-circle-line text-primary-500"></i>
              Ventes du jour
            </h2>
            <Link to="/dashboard/orders" className="text-xs text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer">
              Tout voir
            </Link>
          </header>
          <div className="divide-y divide-background-200/70">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
              </div>
            ) : data.orders.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <i className="ri-shopping-bag-line text-2xl text-foreground-300"></i>
                <p className="text-sm text-foreground-500 mt-2">Aucune vente enregistrée aujourd&apos;hui.</p>
                <p className="text-xs text-foreground-400 mt-1">Partagez votre lien boutique pour lancer la journée.</p>
              </div>
            ) : (
              data.orders.slice(0, 6).map((order) => {
                const amount = parseFloat(String(order.totalttc ?? '0'));
                return (
                  <div key={order.id} className="flex items-center gap-3 px-5 py-3">
                    <span className="w-8 h-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shopping-cart-2-line text-sm"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-900 truncate">
                        {order.nomclient || `Commande #${order.id}`}
                      </p>
                      <p className="text-[11px] text-foreground-400">
                        {timeOf(order.date_time)} · {order.statut_com_vendeur || 'En attente'}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
                      {Number.isNaN(amount) ? '—' : money(amount, order.monaie || currency)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Messages */}
        <section className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-background-200/70">
            <h2 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
              <i className="ri-chat-3-line text-accent-500"></i>
              Messages & demandes
            </h2>
            <Link to="/dashboard/messages" className="text-xs text-accent-700 hover:text-accent-800 font-medium whitespace-nowrap cursor-pointer">
              Ouvrir la messagerie
            </Link>
          </header>
          <div className="divide-y divide-background-200/70">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i>
              </div>
            ) : newMessages === 0 ? (
              <div className="px-5 py-10 text-center">
                <i className="ri-chat-off-line text-2xl text-foreground-300"></i>
                <p className="text-sm text-foreground-500 mt-2">Aucun message reçu aujourd&apos;hui.</p>
              </div>
            ) : (
              <>
                {data.contacts.slice(0, 3).map((c) => (
                  <div key={`c-${c.id}`} className="flex items-start gap-3 px-5 py-3">
                    <span className="w-8 h-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-sm"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-900 truncate">
                        {c.name || 'Visiteur'} <span className="text-xs font-normal text-foreground-400">via le site</span>
                      </p>
                      <p className="text-xs text-foreground-500 line-clamp-2">{c.subject || c.message || 'Demande de contact'}</p>
                    </div>
                    <span className="text-[11px] text-foreground-400 whitespace-nowrap">{timeOf(c.created_at)}</span>
                  </div>
                ))}
                {data.messages.slice(0, 4).map((m) => (
                  <div key={`m-${m.id}`} className="flex items-start gap-3 px-5 py-3">
                    <span className="w-8 h-8 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-3-line text-sm"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground-800 line-clamp-2">{m.message || 'Pièce jointe'}</p>
                      <p className="text-[11px] text-foreground-400 mt-0.5">
                        {m.is_read === 0 ? 'Non lu' : 'Lu'} · {timeOf(m.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>

        {/* Alertes */}
        <section className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden lg:col-span-2">
          <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-background-200/70">
            <h2 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
              <i className="ri-alarm-warning-line text-secondary-600"></i>
              Alertes à traiter
            </h2>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 font-semibold whitespace-nowrap">
              {alertCount} alerte(s)
            </span>
          </header>
          <div className="divide-y divide-background-200/70">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
              </div>
            ) : alertCount === 0 ? (
              <div className="px-5 py-10 text-center">
                <i className="ri-checkbox-circle-line text-2xl text-accent-500"></i>
                <p className="text-sm text-foreground-600 mt-2 font-medium">Tout est à jour, rien à traiter.</p>
                <p className="text-xs text-foreground-400 mt-1">Les nouvelles alertes apparaîtront ici automatiquement.</p>
              </div>
            ) : (
              <>
                {data.pendingOrders > 0 && (
                  <Link to="/dashboard/orders" className="flex items-center gap-3 px-5 py-3 hover:bg-background-100/70 transition-colors cursor-pointer">
                    <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-list-3-line text-sm"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-900">
                        {data.pendingOrders} commande(s) en attente de traitement
                      </p>
                      <p className="text-[11px] text-foreground-400">Confirmez ou refusez-les depuis Commandes.</p>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-300"></i>
                  </Link>
                )}
                {data.stockOut > 0 && (
                  <Link to="/dashboard/gestion-stock" className="flex items-center gap-3 px-5 py-3 hover:bg-background-100/70 transition-colors cursor-pointer">
                    <span className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-sm"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-900">
                        {data.stockOut} produit(s) en rupture de stock
                      </p>
                      <p className="text-[11px] text-foreground-400">Réapprovisionnez pour ne pas perdre de ventes.</p>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-300"></i>
                  </Link>
                )}
                {data.alerts.map((a) => (
                  <div key={a.id} className="flex items-start gap-3 px-5 py-3">
                    <span className="w-8 h-8 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                      <i className="ri-notification-3-line text-sm"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground-800 line-clamp-2">{a.message || 'Nouvelle notification'}</p>
                      <p className="text-[11px] text-foreground-400 mt-0.5">
                        {(a.type || 'system').replace(/_/g, ' ')} · {timeOf(a.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>
      </div>

      {/* Raccourcis de démarrage */}
      <section className="mt-6">
        <h2 className="text-sm font-semibold text-foreground-700 mb-3">Pour bien démarrer</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {[
            { icon: 'ri-add-line', label: 'Ajouter un produit', desc: 'Remplir votre catalogue', to: '/dashboard/products/new' },
            { icon: 'ri-graduation-cap-line', label: 'Mes formations', desc: `${data.grantedCourses} inscrit(s) actif(s)`, to: '/dashboard/formation' },
            { icon: 'ri-calendar-event-line', label: 'Publier un événement', desc: 'Alimenter votre agenda', to: '/dashboard/events' },
            { icon: 'ri-settings-3-line', label: 'Ouvrir ou fermer le site', desc: 'Visibilité de la boutique', to: '/dashboard/settings?tab=site' },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="group bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-300/70 hover:bg-primary-50/30 transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center mb-2 group-hover:bg-primary-100 group-hover:text-primary-700 transition-colors">
                <i className={`${item.icon} text-lg`}></i>
              </span>
              <p className="text-sm font-semibold text-foreground-900">{item.label}</p>
              <p className="text-[11px] text-foreground-500 mt-0.5">{item.desc}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}